import type { PrefillAndIframeAttrsConfig } from "@calcom/embed-core"
import { action } from "@ember/object"
import { later } from "@ember/runloop"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { Group } from "@shared/models/group"
import type { User } from "@shared/models/user"
import { handleError } from "cc-frontend/error-handling"
import groupChangeTitleAction from "cc-frontend/lib/actions/group/GROUP_CHANGE_TITLE"
import groupUnlockGoToGroupAction from "cc-frontend/lib/actions/group/unlock/GROUP_UNLOCK_GO_TO_GROUP"
import groupUnlockScheduleDemoAction from "cc-frontend/lib/actions/group/unlock/GROUP_UNLOCK_SCHEDULE_DEMO"
import type Router from "cc-frontend/router"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type Rpc from "cc-frontend/services/rpc"
import { lockedTeacherCount } from "cc-frontend/utils/group"
import styles from "./admin.module.css"

export interface UnlockGroupFormAdminSignature {
  Args: {
    closeModal: () => void
    model: {
      user: User
      /** Provided by LockedGroup but not by LockedGroupGeneric. */
      group?: Group
    }
  }
}

export default class UnlockGroupFormAdmin extends Component<UnlockGroupFormAdminSignature> {
  readonly styles = styles

  @service declare router: Router
  @service declare rpc: Rpc
  @service("ember-store") declare store: EmberStore
  @service declare dispatcher: Dispatcher

  @tracked
  isMeetingScheduled = false
  @tracked
  isRpcInProgress = false

  createdGroupId: string | null = null

  get calConfig(): PrefillAndIframeAttrsConfig {
    const user = this.args.model.user
    const group = this.args.model.group

    return {
      layout: "month_view",
      name: user.attributes.fullName,
      email: user.attributes.email,
      ATTENDEES_ROLE: user.attributes.title,
      ONBOARDING_GOALS: user.attributes.onboardingGoals.join(", "),
      USER_ADMIN_LINK: `https://www.commonplanner.com/admin/users/${user.id}`,
      ...(group
        ? {
            ATTENDEE_SCHOOL: group.attributes.title,
            ATTENDEE_DISTRICT: group.attributes.cachedPrimaryParentName ?? "",
            POTENTIAL_TEACHERS: lockedTeacherCount(group) + "",
            GROUP_ADMIN_LINK: `https://www.commonplanner.com/admin/groups/${group.id}`,
          }
        : {}),
    }
  }

  @action
  onMeetingScheduled(event: CustomEvent) {
    this.isMeetingScheduled = true

    const booking = event.detail?.data?.booking
    const titleFromBooking: string | undefined = booking?.responses?.ATTENDEE_SCHOOL
    if (titleFromBooking === undefined) {
      // The event we're using is deprecated and might stop providing this info at some point.
      console.error("Could not extract title from booking")
    }

    // Scroll to top so they can see the button.
    $("#unlock-group-form-modal").scrollTop(0)

    void this.unlockSchool(titleFromBooking)

    this.dispatcher.dispatchPreparedAction(
      groupUnlockScheduleDemoAction({
        form: "admin",
        group: this.args.model.group,
        groupTitle: titleFromBooking ?? "Unknown",
      })
    )
  }

  private async unlockSchool(titleFromBooking: string | undefined) {
    this.isRpcInProgress = true
    try {
      if (this.args.model.group !== undefined) {
        const groupId = this.args.model.group.id
        const resp = await this.rpc.group_unlock({
          groupId,
        })
        if (resp.responseOk) {
          // Force the store to refetch the group so that its permissions update to include the user.
          const group: Group = await this.store.find("group", groupId, true)

          if (titleFromBooking !== undefined && titleFromBooking !== group.attributes.title) {
            // Rename the group to match what the user filled out.
            this.dispatcher.dispatchPreparedAction(
              groupChangeTitleAction({
                groupId: this.args.model.group.id,
                value: titleFromBooking,
                originalValue: group.attributes.title,
              })
            )
          }
        } else {
          console.error("Unlocking a group was unsuccessful")
        }
      } else {
        const resp = await this.rpc.group_create_school_v2({
          title: titleFromBooking ?? `${this.args.model.user.attributes.fullName}'s School`,
        })
        if (resp.responseOk) {
          this.createdGroupId = resp.result.groupId
        } else {
          console.error("Creating a school was unsuccessful")
        }
      }

      later(() => {
        if (this.isDestroyed || this.isDestroying) return
        this.isRpcInProgress = false
      }, 250)
    } catch (error) {
      console.error(error)
      handleError(error)
    }
  }

  @action
  goToSchool() {
    if (this.args.model.group) {
      const groupId = this.args.model.group.id
      if (this.args.model.group.attributes.usedAs === "school") {
        this.router.transitionTo("new-group.show.edit.members", groupId)
      } else {
        this.router.transitionTo("new-group.show.edit.admins", groupId)
      }
      this.dispatcher.dispatchPreparedAction(
        groupUnlockGoToGroupAction({
          form: "admin",
          group: this.args.model.group,
          isNewGroup: false,
        })
      )
    } else {
      this.router.transitionTo("new-group.show.edit.members", this.createdGroupId!)
      this.store.find("group", this.createdGroupId!).then((group: Group) => {
        this.dispatcher.dispatchPreparedAction(
          groupUnlockGoToGroupAction({
            form: "admin",
            group,
            isNewGroup: true,
          })
        )
      })
    }

    this.args.closeModal()
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "User::UnlockGroupForm::Admin": typeof UnlockGroupFormAdmin
  }
}
